// Assistant chat message-state machine: applies normalized SSE stream events to the
// message list as a pure reducer (`reduceAssistantMessages`). Owns the tool-event
// descriptor table (`TOOL_EVENT_DESCS`), transient thinking/tool-call placeholder rules,
// start/done event folding, and finalization. Does NOT own side effects — navigation,
// perf marks, error banners, and turn bookkeeping stay in `useAssistantChat.ts`.

import type { CitationObject, StreamDispatchEvent, WebCitationObject } from "@/lib/stream";
import type { AssistantChatMessage, AssistantEvent, AssistantEventType, ResearchReportEvent, ReviewEvent, WorkflowEvent } from "@/components/assistant/types";
import { foldReviewEvent, foldWorkflowEvent } from "@/components/assistant/types";

// Event types that fold a streaming start row into its completed counterpart in mergeEvent.
const MERGE_TYPES = new Set<AssistantEventType>(["doc_read", "doc_find", "doc_created", "doc_edited", "doc_replicated", "web_search", "corpus_search"]);

// Descriptor table for the 8 tool-event pairs: maps each SSE event type to the frontend AssistantEvent
// type and uid prefix. Both start and done events spread their full payload; only type + isStreaming differ.
// Add new tools here — no new case blocks needed.
const TOOL_EVENT_DESCS: Record<string, { type: AssistantEventType; idPrefix: string }> = {
  doc_read_start:       { type: "doc_read",       idPrefix: "read"         },
  doc_read:             { type: "doc_read",       idPrefix: "read"         },
  doc_find_start:       { type: "doc_find",       idPrefix: "find"         },
  doc_find:             { type: "doc_find",       idPrefix: "find"         },
  doc_created_start:    { type: "doc_created",    idPrefix: "created"      },
  doc_created:          { type: "doc_created",    idPrefix: "created"      },
  doc_replicate_start:  { type: "doc_replicated", idPrefix: "replicate"    },
  doc_replicated:       { type: "doc_replicated", idPrefix: "replicate"    },
  doc_edited_start:     { type: "doc_edited",     idPrefix: "edit"         },
  doc_edited:           { type: "doc_edited",     idPrefix: "edit"         },
  web_search_start:     { type: "web_search",     idPrefix: "websearch"    },
  web_search_done:      { type: "web_search",     idPrefix: "websearch"    },
  corpus_search_start:  { type: "corpus_search",  idPrefix: "corpussearch" },
  corpus_search_done:   { type: "corpus_search",  idPrefix: "corpussearch" },
};

export function uid(prefix: string) {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export function assistantMessage(events: AssistantEvent[] = []): AssistantChatMessage {
  return { id: uid("assistant"), role: "assistant", events, annotations: [], isStreaming: true };
}

function stripTransient(events: AssistantEvent[]) {
  return events.filter((event) => !(event.isStreaming && (event.type === "thinking" || event.type === "tool_call_start")));
}

function appendThinking(events: AssistantEvent[]) {
  return [...stripTransient(events), { id: uid("thinking"), type: "thinking", isStreaming: true } satisfies AssistantEvent];
}

function finalizeContentEvents(events: AssistantEvent[]) {
  return events.map((event) => (event.type === "content" && event.isStreaming ? { ...event, isStreaming: false } : event));
}

function finalizeReasoningEvents(events: AssistantEvent[]) {
  return events.map((event) => (event.type === "reasoning" && event.isStreaming ? { ...event, isStreaming: false } : event));
}

export function getEvents(message: AssistantChatMessage) {
  return message.events ?? [];
}

function updateLastAssistant(messages: AssistantChatMessage[], updater: (message: AssistantChatMessage) => AssistantChatMessage) {
  const index = [...messages].reverse().findIndex((message) => message.role === "assistant");
  if (index < 0) {
    return [...messages, updater(assistantMessage())];
  }

  const realIndex = messages.length - 1 - index;
  return messages.map((message, itemIndex) => (itemIndex === realIndex ? updater(message) : message));
}

function mergeEvent(events: AssistantEvent[], next: AssistantEvent) {
  const candidates = [...events];
  const startType = MERGE_TYPES.has(next.type) ? next.type : null;

  if (!startType) {
    return [...stripTransient(candidates), next];
  }

  for (let i = candidates.length - 1; i >= 0; i -= 1) {
    const candidate = candidates[i];
    if (candidate.type === startType && candidate.isStreaming) {
      candidates[i] = { ...candidate, ...next, id: candidate.id, isStreaming: false } as AssistantEvent;
      return candidates;
    }
  }

  return [...stripTransient(candidates), next];
}

function normalizeAnnotations(citations: Array<CitationObject | WebCitationObject>, events: AssistantEvent[]) {
  const edits = events.flatMap((event) => (event.type === "doc_edited" && event.annotations ? event.annotations : []));
  return [...citations, ...edits];
}

function findLastIndexOfType(events: AssistantEvent[], type: AssistantEventType) {
  for (let i = events.length - 1; i >= 0; i -= 1) {
    if (events[i].type === type) {
      return i;
    }
  }
  return -1;
}

/** Finalize the trailing assistant message: close open content/reasoning blocks and drop transient rows. */
export function finalizeAssistantMessages(messages: AssistantChatMessage[]) {
  return updateLastAssistant(messages, (message) => ({
    ...message,
    events: stripTransient(finalizeReasoningEvents(finalizeContentEvents(getEvents(message)))),
    isStreaming: false
  }));
}

/**
 * Pure reducer applying one normalized stream event to the message list. Transport-only
 * events (`chat_id`, `turn_meta`) and other side effects (navigation, perf marks, error
 * banners) are handled by the hook; this owns only the message-state machine.
 */
export function reduceAssistantMessages(messages: AssistantChatMessage[], event: StreamDispatchEvent): AssistantChatMessage[] {
  if (event.type === "error" || event.type === "turn_failed") {
    return updateLastAssistant(messages, (message) => ({ ...message, error: event.message, isStreaming: false }));
  }

  switch (event.type) {
    case "content_delta":
      return updateLastAssistant(messages, (message) => {
        let events = getEvents(message);
        const last = events.at(-1);
        if (!last || last.type !== "content" || !last.isStreaming) {
          events = [
            ...stripTransient(finalizeReasoningEvents(finalizeContentEvents(events))),
            { id: uid("content"), type: "content", text: event.text, isStreaming: true }
          ];
        } else {
          events = events.map((e, i) => (i === events.length - 1 && e.type === "content" ? { ...e, text: e.text + event.text } : e));
        }
        return { ...message, events };
      });
    case "content_done":
      return updateLastAssistant(messages, (message) => ({
        ...message,
        events: finalizeContentEvents(getEvents(message))
      }));
    case "reasoning_delta":
      return updateLastAssistant(messages, (message) => {
        let events = stripTransient(finalizeContentEvents(getEvents(message)));
        const last = events.at(-1);
        if (last?.type === "reasoning" && last.isStreaming) {
          events = events.map((item, index) => (index === events.length - 1 && item.type === "reasoning" ? { ...item, text: `${item.text}${event.text}` } : item));
        } else {
          events = [...finalizeReasoningEvents(events), { id: uid("reasoning"), type: "reasoning", text: event.text, isStreaming: true }];
        }
        return { ...message, events };
      });
    case "reasoning_block_end":
      return updateLastAssistant(messages, (message) => ({ ...message, events: appendThinking(finalizeReasoningEvents(getEvents(message))) }));
    case "tool_call_start":
      return updateLastAssistant(messages, (message) => ({
        ...message,
        events: [...stripTransient(finalizeContentEvents(getEvents(message))), { id: uid("tool"), type: "tool_call_start", name: event.name, isStreaming: true }]
      }));
    // Descriptor table: 8 tool-event pairs share the same start/done shape — new tools become a table entry in TOOL_EVENT_DESCS.
    case "doc_read_start":
    case "doc_find_start":
    case "doc_created_start":
    case "doc_replicate_start":
    case "doc_edited_start":
    case "web_search_start":
    case "corpus_search_start": {
      const desc = TOOL_EVENT_DESCS[event.type];
      const raw = event as Record<string, unknown>;
      return updateLastAssistant(messages, (message) => ({
        ...message,
        events: [...stripTransient(finalizeContentEvents(getEvents(message))), { ...raw, id: uid(desc.idPrefix), type: desc.type, isStreaming: true } as AssistantEvent]
      }));
    }
    case "doc_read":
    case "doc_find":
    case "doc_created":
    case "doc_replicated":
    case "doc_edited":
    case "web_search_done":
    case "corpus_search_done": {
      const desc = TOOL_EVENT_DESCS[event.type];
      const raw = event as Record<string, unknown>;
      return updateLastAssistant(messages, (message) => ({
        ...message,
        events: appendThinking(mergeEvent(getEvents(message), { ...raw, id: uid(desc.idPrefix), type: desc.type, isStreaming: false } as AssistantEvent))
      }));
    }
    case "doc_download":
      return updateLastAssistant(messages, (message) => ({
        ...message,
        events: [...stripTransient(finalizeContentEvents(getEvents(message))), { id: uid("download"), type: "doc_download", filename: event.filename, download_url: event.download_url }]
      }));
    case "workflow_started":
    case "workflow_phase":
    case "task_started":
    case "task_done":
    case "task_refuted":
    case "workflow_progress":
    case "workflow_done":
    case "workflow_budget_exhausted": {
      const raw = event as unknown as Record<string, unknown>;
      return updateLastAssistant(messages, (message) => {
        const events = getEvents(message);
        const index = findLastIndexOfType(events, "workflow");
        if (index >= 0) {
          const prev = events[index] as WorkflowEvent;
          const folded = { ...foldWorkflowEvent(prev, raw), id: prev.id };
          return { ...message, events: events.map((e, i) => (i === index ? folded : e)) };
        }
        const created = { ...foldWorkflowEvent(null, raw), id: uid("workflow") };
        return { ...message, events: [...stripTransient(finalizeContentEvents(events)), created] };
      });
    }
    case "review_started":
    case "claim_verdict":
    case "redraft_started":
    case "review_done": {
      const raw = event as unknown as Record<string, unknown>;
      return updateLastAssistant(messages, (message) => {
        const events = getEvents(message);
        const index = findLastIndexOfType(events, "review");
        if (index >= 0) {
          const prev = events[index] as ReviewEvent;
          const folded = { ...foldReviewEvent(prev, raw), id: prev.id };
          return { ...message, events: events.map((e, i) => (i === index ? folded : e)) };
        }
        const created = { ...foldReviewEvent(null, raw), id: uid("review") };
        return { ...message, events: [...stripTransient(finalizeContentEvents(events)), created] };
      });
    }
    case "answer_revised": {
      // Silent in-place swap of the shown answer (no before/after diff).
      const revised = event.content;
      return updateLastAssistant(messages, (message) => {
        const events = getEvents(message);
        const index = findLastIndexOfType(events, "content");
        if (index < 0) return message;
        return { ...message, events: events.map((e, i) => (i === index && e.type === "content" ? { ...e, text: revised } : e)) };
      });
    }
    case "research_report": {
      const reportEvent: ResearchReportEvent = {
        id: uid("report"),
        type: "research_report",
        report: event.report as unknown as ResearchReportEvent["report"],
      };
      return updateLastAssistant(messages, (message) => ({ ...message, events: [...getEvents(message), reportEvent] }));
    }
    case "turn_done":
      return updateLastAssistant(messages, (message) => ({ ...message, duration_ms: event.duration_ms }));
    case "citations":
      return updateLastAssistant(messages, (message) => {
        const events = stripTransient(finalizeReasoningEvents(finalizeContentEvents(getEvents(message))));
        return { ...message, events, annotations: normalizeAnnotations(event.citations, events), isStreaming: false };
      });
    default:
      return messages;
  }
}
